import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  useToast,
  Spinner,
  Divider,
  Badge,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { 
  FiUser, 
  FiCalendar, 
  FiClock, 
  FiFileText, 
  FiPlus,
  FiRefreshCw,
  FiSearch
} from "react-icons/fi";
import { useAssignOvertime } from "../../../services/adminOvertimeService";
import { useGetEmployees } from "../../../services/employeeService";

const OvertimeAssignment = () => {
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    hours_requested: 1.0,
    reason: '',
    is_immediate_approval: true
  });

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const toast = useToast();

  // Load employees for selection
  const {
    getEmployeesData: employeesData,
    getEmployeesError: employeesError,
    getEmployeesIsLoading: employeesIsLoading,
    refetchGetEmployees: refetchEmployees
  } = useGetEmployees({ enabled: true });

  // Assign overtime
  const {
    assignOvertime,
    assignOvertimeIsLoading,
    assignOvertimeError
  } = useAssignOvertime(() => {
    toast({
      title: "Overtime Assigned",
      description: "Overtime has been successfully assigned to the employee.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    // Reset form
    setFormData({
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      hours_requested: 1.0,
      reason: '',
      is_immediate_approval: true
    });
    setSelectedEmployee(null);
  });

  // Get employees array
  const employeesArray = employeesData?.employees || employeesData || [];



  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setFormData(prev => ({
      ...prev,
      employee_id: employee.employee_id
    }));
  };

  const handleSubmit = () => {

    if (!formData.employee_id || !formData.date || !formData.hours_requested) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      employeeId: formData.employee_id,
      payload: {
        date: formData.date,
        hours_requested: parseFloat(formData.hours_requested),
        reason: formData.reason,
        is_immediate_approval: formData.is_immediate_approval
      }
    };

    assignOvertime(payload);
  };

  const handleReset = () => {
    setFormData({
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      hours_requested: 1.0,
      reason: '',
      is_immediate_approval: true
    });
    setSelectedEmployee(null);
  };

  if (employeesIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4}>Loading employees...</Text>
      </Box>
    );
  }

  if (employeesError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error Loading Employees</AlertTitle>
        <AlertDescription>
          {employeesError.message || "Failed to load employees. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Error Alert */}
      {assignOvertimeError && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Assignment Failed</AlertTitle>
          <AlertDescription>
            {assignOvertimeError.message || "Failed to assign overtime. Please try again."}
          </AlertDescription>
        </Alert>
      )}

      {/* Assignment Form */}
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="600" color="gray.800" mb={2}>
                Manual Overtime Assignment
              </Text>
              <Text fontSize="sm" color="gray.600">
                Directly assign overtime hours to employees without requiring a request
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                             {/* Employee Selection */}
               <FormControl isRequired>
                 <FormLabel>Select Employee</FormLabel>
                 <Select
                   placeholder="Select Employee"
                   value={formData.employee_id}
                   onChange={(e) => {
                     const employeeId = e.target.value;
                     const employee = employeesArray.find(emp => emp.employee_id === employeeId);
                     handleEmployeeSelect(employee);
                   }}
                 >
                                       {(employeesData?.employees || []).map((employee) => (
                      <option key={employee.employee_id} value={employee.employee_id}>
                        {employee.first_name} {employee.last_name} - {employee.employee_id}
                      </option>
                    ))}
                 </Select>
               </FormControl>

              {/* Date */}
              <FormControl isRequired>
                <FormLabel>Overtime Date</FormLabel>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                />
              </FormControl>

              {/* Hours */}
              <FormControl isRequired>
                <FormLabel>Hours to Assign</FormLabel>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={formData.hours_requested}
                  onChange={(e) => handleInputChange('hours_requested', parseFloat(e.target.value))}
                  placeholder="1.0"
                />
              </FormControl>

              {/* Immediate Approval */}
              <FormControl>
                <FormLabel>Approval Status</FormLabel>
                <Select
                  value={formData.is_immediate_approval ? 'approved' : 'pending'}
                  onChange={(e) => handleInputChange('is_immediate_approval', e.target.value === 'approved')}
                >
                  <option value="approved">Immediately Approved</option>
                  <option value="pending">Requires Approval</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            {/* Reason */}
            <FormControl>
              <FormLabel>Reason for Assignment</FormLabel>
              <Textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder="Enter the reason for this overtime assignment..."
                rows={3}
              />
            </FormControl>

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end">
              <Button
                leftIcon={<FiRefreshCw />}
                variant="outline"
                onClick={handleReset}
                isDisabled={assignOvertimeIsLoading}
              >
                Reset
              </Button>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={assignOvertimeIsLoading}
                loadingText="Assigning..."
                isDisabled={!formData.employee_id}
              >
                Assign Overtime
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Selected Employee Info */}
      {selectedEmployee && (
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                Selected Employee
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <HStack spacing={2}>
                    <FiUser size={16} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">Name</Text>
                  </HStack>
                                     <Text fontWeight="600">{`${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`.trim()}</Text>
                </Box>
                
                <Box>
                  <HStack spacing={2}>
                    <FiUser size={16} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">Employee ID</Text>
                  </HStack>
                  <Text fontWeight="600">{selectedEmployee.employee_id}</Text>
                </Box>
                
                <Box>
                  <HStack spacing={2}>
                    <FiUser size={16} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">Department</Text>
                  </HStack>
                  <Text fontWeight="600">{selectedEmployee.department}</Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Assignment Summary */}
      {formData.employee_id && formData.hours_requested && (
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                Assignment Summary
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600">Employee</Text>
                                     <Text fontWeight="600">{selectedEmployee ? `${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`.trim() : 'Not selected'}</Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600">Date</Text>
                  <Text fontWeight="600">{new Date(formData.date).toLocaleDateString()}</Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600">Hours</Text>
                  <Text fontWeight="600" color="blue.600">
                    {formData.hours_requested} hours
                  </Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600">Status</Text>
                  <Badge 
                    colorScheme={formData.is_immediate_approval ? 'green' : 'yellow'}
                    variant="subtle"
                  >
                    {formData.is_immediate_approval ? 'Immediately Approved' : 'Pending Approval'}
                  </Badge>
                </Box>
              </SimpleGrid>
              
              {formData.reason && (
                <Box>
                  <Text fontSize="sm" color="gray.600">Reason</Text>
                  <Text fontSize="sm" p={3} bg="gray.50" borderRadius="md">
                    {formData.reason}
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default OvertimeAssignment; 